import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Live, per-breakpoint logo height controls (in px) so we can fine-tune
 * the brand mark across mobile / tablet / desktop without redeploying.
 *
 * Breakpoints (match Tailwind):
 *   navMobile : < 640px   (phones)
 *   navSm     : 640-767px (large phones / small tablets portrait)
 *   navTablet : 768-1023px (tablets, md)
 *   navDesktop: >= 1024px (desktop, lg+)
 *
 * Footer has its own mobile + desktop tiers so the wide horizontal logo
 * doesn't blow out narrow footer columns.
 */
export type LogoSlot =
  | "navMobile"
  | "navSm"
  | "navTablet"
  | "navDesktop"
  | "footerMobile"
  | "footer";

export interface LogoSizes {
  navMobile: number;
  navSm: number;
  navTablet: number;
  navDesktop: number;
  footerMobile: number;
  footer: number;
  taglineNav: number;
  taglineFooter: number;
}

const DEFAULTS: LogoSizes = {
  navMobile: 50,
  navSm: 54,
  navTablet: 58,
  navDesktop: 58,
  footerMobile: 42,
  footer: 42,
  taglineNav: 14,
  taglineFooter: 16,
};

const STORAGE_KEY = "gpv:logo-sizes:v16";

interface Ctx {
  sizes: LogoSizes;
  setSize: (slot: keyof LogoSizes, value: number) => void;
  reset: () => void;
}

const LogoSizeContext = createContext<Ctx | null>(null);

export const LogoSizeProvider = ({ children }: { children: React.ReactNode }) => {
  const [sizes, setSizes] = useState<LogoSizes>(() => {
    if (typeof window === "undefined") return DEFAULTS;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULTS;
      return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<LogoSizes>) };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
    } catch {
      /* ignore quota errors */
    }
  }, [sizes]);

  const setSize = useCallback((slot: keyof LogoSizes, value: number) => {
    setSizes((prev) => ({ ...prev, [slot]: value }));
  }, []);

  const reset = useCallback(() => setSizes(DEFAULTS), []);

  const value = useMemo(() => ({ sizes, setSize, reset }), [sizes, setSize, reset]);
  return <LogoSizeContext.Provider value={value}>{children}</LogoSizeContext.Provider>;
};

export const useLogoSizes = () => {
  const ctx = useContext(LogoSizeContext);
  if (!ctx) throw new Error("useLogoSizes must be used within LogoSizeProvider");
  return ctx;
};

/** Resolve the current nav logo height in px based on viewport width. */
export const useNavLogoHeight = () => {
  const { sizes } = useLogoSizes();
  const [h, setH] = useState<number>(sizes.navMobile);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1024) setH(sizes.navDesktop);
      else if (w >= 768) setH(sizes.navTablet);
      else if (w >= 640) setH(sizes.navSm);
      else setH(sizes.navMobile);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [sizes]);

  return h;
};

/** Resolve the current footer logo height in px based on viewport width. */
export const useFooterLogoHeight = () => {
  const { sizes } = useLogoSizes();
  const [h, setH] = useState<number>(sizes.footerMobile);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 768) setH(sizes.footer);
      else setH(sizes.footerMobile);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [sizes]);

  return h;
};
